import React, { useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import { inviteUser } from "../../api/webUserAPI";
import { useTranslation } from "react-i18next";

export default function InviteUserDialog({ open, onClose, onSuccess }) {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({ email: "", roles: "" });
  const [formErrors, setFormErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const handleClose = () => {
    setFormData({ email: "", roles: "" });
    setFormErrors({});
    onClose();
  };

  const handleFormChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (formErrors[field]) {
      setFormErrors((prev) => ({ ...prev, [field]: null }));
    }
  };

  const handleRoleChange = (event) => {
    const value = event.target.value;
    setFormData((prev) => ({ ...prev, roles: value }));
    if (value && value.trim() !== "" && formErrors.roles) {
      setFormErrors((prev) => ({ ...prev, roles: null }));
    }
  };

  const validate = () => {
    const errors = {};
    if (!formData.email || !formData.email.trim()) {
      errors.email =
        t("users.messages.validation.emailRequired") || "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email =
        t("users.messages.validation.emailInvalid") ||
        "Please enter a valid email address";
    }
    if (!formData.roles || formData.roles.trim() === "") {
      errors.roles =
        t("users.messages.validation.rolesRequired") || "A role is required";
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    try {
      setLoading(true);
      const dataToSend = {
        email: formData.email,
        role: formData.roles,
      };
      const response = await inviteUser(dataToSend);
      if (response.data && response.data.returnCode === -1) {
        throw new Error(response.data.exMessage || "Add user failed");
      }
      console.log("User added successfully");
      handleClose();
      if (onSuccess) onSuccess();
    } catch (error) {
      console.error("Error adding user:", error);
      setFormErrors({ submit: error.message || "Failed to add user" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t("users.dialogs.add.title")}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
          {formErrors.submit && (
            <Alert severity="error" sx={{ mb: 1 }}>
              {formErrors.submit}
            </Alert>
          )}
          <TextField
            label={t("users.fields.email")}
            value={formData.email}
            onChange={(e) => handleFormChange("email", e.target.value)}
            fullWidth
            type="email"
            required
            error={Boolean(formErrors.email)}
            helperText={formErrors.email}
            autoFocus
          />
          <FormControl fullWidth required error={Boolean(formErrors.roles)}>
            <InputLabel>{t("users.fields.roles")}</InputLabel>
            <Select
              value={formData.roles}
              onChange={handleRoleChange}
              label={t("users.fields.roles")}
            >
              <MenuItem value="user">{t("users.roles.user")}</MenuItem>
              <MenuItem value="admin">{t("users.roles.admin")}</MenuItem>
              <MenuItem value="serviceUser">
                {t("users.roles.serviceUser")}
              </MenuItem>
            </Select>
            {formErrors.roles && (
              <Typography
                color="error"
                variant="caption"
                sx={{ mt: 0.5, ml: 1.5 }}
              >
                {formErrors.roles}
              </Typography>
            )}
          </FormControl>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>{t("common.cancel")}</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={loading}>
          {loading ? (
            <CircularProgress size={20} />
          ) : (
            t("common.add") || "Davet Et"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
